import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, fetchApi } from '@/lib/api';
import type { Notification, NotificationScope, NotificationType } from './types';

const NOTIFICATIONS_KEY = ['notifications'] as const;

function itemNotificationsKey(itemUin: number) {
  return [...NOTIFICATIONS_KEY, 'item', itemUin] as const;
}

function tagsNotificationKey(tags: number[], brandIds?: number[] | null) {
  return [
    ...NOTIFICATIONS_KEY,
    'tags',
    [...tags].sort((a, b) => a - b),
    brandIds ? [...brandIds].sort((a, b) => a - b) : null,
  ] as const;
}

/**
 * Fetch all notifications for the current user.
 */
export function useNotifications(enabled = true) {
  return useInfiniteQuery({
    queryKey: NOTIFICATIONS_KEY,
    queryFn: async ({ pageParam }) => {
      const data = await fetchApi(
        api.api.notifications.get({ query: { page: pageParam, limit: 20 } }),
      );
      return data as { items: Notification[]; page: number; hasNextPage: boolean };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.hasNextPage ? lastPage.page + 1 : undefined),
    enabled,
  });
}

/**
 * Fetch all item-scoped notifications for the current user as a lookup map.
 * Returns Map<itemUin, Notification> — single request replaces N per-item fetches.
 */
export function useItemNotificationMap(enabled = true) {
  return useQuery({
    queryKey: [...NOTIFICATIONS_KEY, 'item-map'] as const,
    queryFn: async () => {
      const data = await fetchApi(api.api.notifications['item-map'].get());
      const record = data as Record<string, Notification>;
      return new Map(Object.entries(record).map(([k, v]) => [Number(k), v]));
    },
    enabled,
  });
}

/**
 * Fetch notifications for a specific item.
 * Uses the dedicated GET /notifications/item/:itemUin endpoint.
 */
export function useItemNotification(itemUin: number, enabled = true) {
  return useQuery({
    queryKey: itemNotificationsKey(itemUin),
    queryFn: async () => {
      const data = await fetchApi(api.api.notifications.item({ itemUin: String(itemUin) }).get());
      const notifications = data as Notification[];
      return notifications[0] ?? null;
    },
    enabled,
  });
}

/**
 * Fetch notification for a specific set of tags.
 * Uses GET /notifications/tags?tags=1,2,3&brandIds=4,5
 */
export function useTagsNotification(tags: number[], brandIds?: number[] | null, enabled = true) {
  const sortedTags = [...tags].sort((a, b) => a - b);
  const sortedBrands = brandIds ? [...new Set(brandIds)].sort((a, b) => a - b) : [];
  return useQuery({
    queryKey: tagsNotificationKey(sortedTags, sortedBrands.length > 0 ? sortedBrands : null),
    queryFn: async () => {
      const queryParams: Record<string, string> = {};
      if (sortedTags.length > 0) queryParams.tags = sortedTags.join(',');
      if (sortedBrands.length > 0) queryParams.brandIds = sortedBrands.join(',');
      const data = await fetchApi(api.api.notifications.tags.get({ query: queryParams }));
      const notifications = data as Notification[];
      return notifications[0] ?? null;
    },
    enabled: enabled && (sortedTags.length > 0 || sortedBrands.length > 0),
  });
}

/**
 * Create a new notification. Invalidates both the global and item-specific caches.
 */
export function useCreateNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: {
      scope: NotificationScope;
      type: NotificationType;
      itemUin?: number;
      tags?: number[];
      brandIds?: number[];
      priceThreshold?: number;
      expiresAt?: string;
    }) => {
      const data = await fetchApi(api.api.notifications.post(body));
      return data as Notification;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
      if (variables.itemUin) {
        queryClient.invalidateQueries({
          queryKey: itemNotificationsKey(variables.itemUin),
        });
      }
      if (variables.scope === 'TAGS' && variables.tags && variables.tags.length > 0) {
        queryClient.invalidateQueries({
          queryKey: tagsNotificationKey(variables.tags, variables.brandIds ?? null),
        });
      }
    },
  });
}

/**
 * Update an existing notification. Invalidates both the global and item-specific caches.
 */
export function useUpdateNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: {
      id: string;
      type?: NotificationType;
      priceThreshold?: number;
      expiresAt?: string | null;
      active?: boolean;
    }) => {
      const data = await fetchApi(api.api.notifications({ id }).put(body));
      return data as Notification;
    },
    onSuccess: (updatedNotification) => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
      if (updatedNotification.itemUin) {
        queryClient.invalidateQueries({
          queryKey: itemNotificationsKey(updatedNotification.itemUin),
        });
      }
      if (updatedNotification.tags && updatedNotification.tags.length > 0) {
        queryClient.invalidateQueries({
          queryKey: tagsNotificationKey(
            updatedNotification.tags,
            updatedNotification.brandIds ?? null,
          ),
        });
      }
    },
  });
}

/**
 * Delete a notification. Invalidates both the global and item-specific caches.
 */
export function useDeleteNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      itemUin,
      tags,
      brandIds,
    }: {
      id: string;
      itemUin?: number | null;
      tags?: number[] | null;
      brandIds?: number[] | null;
    }) => {
      await fetchApi(api.api.notifications({ id }).delete());
      return { id, itemUin, tags, brandIds };
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
      if (variables.itemUin) {
        queryClient.invalidateQueries({
          queryKey: itemNotificationsKey(variables.itemUin),
        });
      }
      if (variables.tags && variables.tags.length > 0) {
        queryClient.invalidateQueries({
          queryKey: tagsNotificationKey(variables.tags, variables.brandIds ?? null),
        });
      }
    },
  });
}
